import { http } from '@/lib/axios';
import { toApiError } from '@/lib/unwrap';
import type { AppliedEvent, Slot } from '@/types/product.slot';

interface RawSlot extends Partial<Slot> {
  id: string;
  remaining?: number;
  status: string;
}

/**
 * Normalize a raw slot. The backend exposes the available count as
 * `remaining`; the rest of the app reads `availableCount`. Ported from
 * reference `ticketSchedule.js#normalizeSlot`.
 */
function normalizeSlot(slot: RawSlot): Slot {
  let availableCount = slot.availableCount;
  if (availableCount === undefined) {
    if (typeof slot.remaining === 'number') {
      availableCount = slot.remaining;
    } else if (typeof slot.capacity === 'number') {
      availableCount = Math.max(
        0,
        slot.capacity - (slot.reservedCount ?? 0) - (slot.soldCount ?? 0),
      );
    } else {
      availableCount = 0;
    }
  }
  return { ...slot, availableCount } as Slot;
}

interface AvailabilityShape {
  slots?: RawSlot[];
  data?: { slots?: RawSlot[] } | RawSlot[];
  appliedEvent?: AppliedEvent;
}

/** Defensive extractor handling envelope, plain array, and nested shapes. */
export function extractSlots(payload: unknown): Slot[] {
  if (!payload) return [];
  let raw: RawSlot[] = [];
  if (Array.isArray(payload)) {
    raw = payload as RawSlot[];
  } else {
    const p = payload as AvailabilityShape;
    if (Array.isArray(p.slots)) raw = p.slots;
    else if (Array.isArray(p.data)) raw = p.data;
    else if (p.data && Array.isArray(p.data.slots)) raw = p.data.slots;
  }
  return raw.map(normalizeSlot);
}

export function extractAppliedEvent(payload: unknown): AppliedEvent | null {
  if (!payload || typeof payload !== 'object') return null;
  const p = payload as AvailabilityShape & { data?: { appliedEvent?: AppliedEvent } };
  if (p.appliedEvent) return p.appliedEvent;
  if (p.data && !Array.isArray(p.data) && p.data.appliedEvent) return p.data.appliedEvent;
  return null;
}

export interface AvailabilityResult {
  slots: Slot[];
  appliedEvent: AppliedEvent | null;
}

/** GET /ticket-schedule/:productId/availability/:date */
export async function getProductAvailability(
  productId: string,
  date: string,
): Promise<AvailabilityResult> {
  try {
    const res = await http.get(`/ticket-schedule/${productId}/availability/${date}`);
    return {
      slots: extractSlots(res.data),
      appliedEvent: extractAppliedEvent(res.data),
    };
  } catch (error) {
    throw toApiError(error);
  }
}
